/**
 * DraftingVersionsPane - the version history, as a pane body.
 *
 * This was `DraftingVersionsRail`: a `w-80 shrink-0 border-s bg-white` column
 * tiled inside the content row, drawing its own header, its own close button
 * and its own "current" chip. The `Panel` around it draws all three now, so
 * what is left here is the list - see `shared/surface/README.md`, "Pane bodies
 * bring no chrome of their own".
 *
 * WHY THE ROWS ARE NOT `ListRow`s. A snapshot is a chronology entry, not a
 * directory entry: it is WHO wrote it, WHEN, and how big - three facts that do
 * not fit ListRow's one truncating meta line, and an avatar that cannot live
 * inside its `<p>`. So the row has the same anatomy as `activity/ActivityPane`
 * (glyph disc on a rail, a quiet surface beside it, actor + relative time
 * underneath), built from the same primitives - Well, Badge, ProfileImage,
 * `feed/feedTime` - because a version history and an activity log are the same
 * family of list at different volumes.
 *
 * State comes from `useDraftingVersions`, which the PAGE owns: the canvas
 * shows whichever version is being previewed, so preview cannot live down
 * here.
 */

import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  PiArrowCounterClockwiseLight,
  PiArrowsLeftRightLight,
  PiClockCounterClockwiseLight,
  PiEyeLight,
} from 'react-icons/pi';
import VersionCompareOverlay from './VersionCompareOverlay';
import PaneBody from '../../shared/surface/PaneBody';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import ProfileImage from '../../Header/ProfileImage';
import { Well } from '../../shared/brand/Field';
import { feedTimeLabel } from '../../shared/feed/feedTime';
import { actorInitials } from '../../shared/activity/activityModel';
import type { DraftingVersion } from '../../../store/slices/draftingSlice';
import type { DraftingVersionsState } from './useDraftingVersions';

const fmtBytes = (n: number | null): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

interface VersionActor {
  name: string;
  email: string | null;
}

/**
 * Who wrote this snapshot, when the blob record says.
 *
 * Blob versioning is storage metadata: the row that comes back carries
 * `version_id`, `last_modified` and `size_bytes` for certain, and an author
 * only where the backend has stamped one. Read the shapes it may arrive in and
 * render the author line only when there IS one - an "Unknown" avatar on every
 * row is worse than no avatar at all.
 */
const versionActor = (version: DraftingVersion): VersionActor | null => {
  const raw = version as unknown as Record<string, unknown>;
  const pick = (...keys: string[]): string | null => {
    for (const key of keys) {
      const value = raw[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  };
  const name = pick('author_name', 'created_by_name', 'user_name', 'modified_by', 'created_by');
  if (!name) return null;
  return { name, email: pick('author_email', 'user_email', 'created_by_email') };
};

interface VersionRowProps {
  version: DraftingVersion;
  label: string;
  isCurrent: boolean;
  selected: boolean;
  restoring: boolean;
  restoreDisabled: boolean;
  index: number;
  onSelect: () => void;
  onRestore: () => void;
  onCompare: () => void;
}

/** One snapshot: glyph disc on the rail, the record as a well beside it. */
const VersionRow: React.FC<VersionRowProps> = ({
  version,
  label,
  isCurrent,
  selected,
  restoring,
  restoreDisabled,
  index,
  onSelect,
  onRestore,
  onCompare,
}) => {
  const { t } = useTranslation();
  const time = feedTimeLabel(version.last_modified ?? undefined, t);
  const size = fmtBytes(version.size_bytes);
  const actor = versionActor(version);

  return (
    <li
      className="reveal-item relative ps-9"
      style={{ '--reveal-delay': `${Math.min(index, 8) * 30}ms` } as React.CSSProperties}
    >
      <span
        aria-hidden
        className={clsx(
          'absolute start-0 top-2.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border',
          'transition-colors duration-200 ease-slide [&>svg]:h-3.5 [&>svg]:w-3.5',
          selected || isCurrent
            ? 'border-champagne/50 bg-champagne/10 text-champagne'
            : 'border-hairline bg-surface text-fg-muted'
        )}
      >
        <PiClockCounterClockwiseLight />
      </span>

      <Well
        data-testid={`drafting-version-${version.version_id}`}
        data-selected={selected}
        className={clsx(
          // `!` because Well brings its own padding/surface - these replace it
          // rather than sitting beside it, and Tailwind's emit order decides
          // otherwise.
          // Selected = a neutral raised surface with a champagne LEFT-RAIL accent
          // (champagne is emphasis only - never a filled panel).
          'group relative !p-0 transition-colors duration-200 ease-slide',
          selected
            ? '!bg-surface-soft !border-hairline border-s-2 !border-s-champagne'
            : 'hover:bg-surface-soft'
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          // The reveal sits over the trailing space, so a restorable row keeps
          // that space clear of its own text.
          className={clsx('w-full px-2.5 py-2 text-start', !isCurrent && 'pe-[124px]')}
          aria-pressed={selected}
        >
          <div className="flex items-center gap-2">
            <span
              className={clsx(
                'min-w-0 flex-1 truncate text-[13px] leading-snug transition-colors duration-200 ease-slide',
                selected ? 'font-semibold text-fg-strong' : 'font-medium text-fg-body'
              )}
            >
              {label}
            </span>
            {isCurrent && (
              <Badge color="green" className="flex-shrink-0 text-[10px]">
                {t('Current')}
              </Badge>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-fg-muted">
            {selected && !isCurrent && (
              <Badge color="yellow" className="flex-shrink-0 text-[10px]">
                {t('Previewing')}
              </Badge>
            )}
            {actor && (
              <span className="flex min-w-0 items-center gap-1.5">
                <ProfileImage
                  profilePicture={null}
                  email={actor.email}
                  initials={actorInitials(actor.name)}
                  name={actor.name}
                  size="w-5 h-5"
                />
                <span className="truncate font-medium text-fg-body">{actor.name}</span>
              </span>
            )}
            {time.label && <span title={time.title}>{time.label}</span>}
            {size && (
              <span className="text-fg-muted/80">
                <span aria-hidden className="me-2 text-fg-muted/60">·</span>
                {size}
              </span>
            )}
          </div>
        </button>

        {/* Revealed, not stacked - absolutely positioned so a row is the same
            height whether or not it can be restored, and the list keeps its
            rhythm when the pointer moves down it. */}
        {!isCurrent && (
          <div
            className="absolute end-2 top-1.5 flex items-center gap-1 opacity-0 transition-opacity duration-200
                       ease-slide group-hover:opacity-100 focus-within:opacity-100"
          >
            <Button
              variant="neutral"
              size="small"
              onClick={onCompare}
              tooltip={t('Compare with current')}
              ariaLabel={t('Compare with current')}
              icon={<PiArrowsLeftRightLight size={13} />}
            />
            <Button
              variant="neutral"
              size="small"
              disabled={restoreDisabled}
              isLoading={restoring}
              icon={restoring ? undefined : <PiArrowCounterClockwiseLight size={13} />}
              onClick={onRestore}
            >
              {t('Restore')}
            </Button>
          </div>
        )}
      </Well>
    </li>
  );
};

interface Props {
  versions: DraftingVersionsState;
}

const DraftingVersionsPane: React.FC<Props> = ({ versions: state }) => {
  const { t } = useTranslation();
  const {
    versions, kind, uuid, loading, available, previewVersionId, restoringId, selectVersion, restore,
  } = state;

  // The version being compared against current (the redline overlay). Null = closed.
  const [compareId, setCompareId] = useState<string | null>(null);
  const currentVersionId = versions[0]?.version_id;
  const compareLabel = (() => {
    if (!compareId) return '';
    const idx = versions.findIndex((v) => v.version_id === compareId);
    return idx < 0 ? t('Selected version') : t('Version {{n}}', { n: versions.length - idx });
  })();

  return (
    <PaneBody>
      {previewVersionId && (
        <div className="shrink-0 px-3 pt-3">
          <Well className="flex items-center justify-between gap-2 text-xs text-warning-800 dark:text-warning-200">
            <span className="flex items-center gap-1.5">
              <PiEyeLight size={13} /> {t('Previewing an older version')}
            </span>
            <Button variant="neutral" size="small" onClick={() => selectVersion(null)}>
              {t('Back to current')}
            </Button>
          </Well>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar-main px-3 py-3">
        {loading && <Skeleton variant="list" rows={4} />}

        {!loading && !available && (
          <EmptyState
            density="compact"
            icon={<PiClockCounterClockwiseLight />}
            headline={t('No version history')}
            subtext={t('No version history is available for this document yet.')}
          />
        )}

        {!loading && available && versions.length === 0 && (
          <EmptyState
            density="compact"
            icon={<PiClockCounterClockwiseLight />}
            headline={t('No versions yet')}
            subtext={t('A snapshot is kept every time this document is generated or restored.')}
          />
        )}

        {!loading && available && versions.length > 0 && (
          <ol className="relative space-y-2.5">
            {/* The rail the glyph discs sit on. */}
            <span aria-hidden className="absolute start-3 top-2 bottom-2 w-px bg-hairline-soft" />
            {versions.map((v, idx) => {
              const isCurrent = idx === 0;
              const isPreviewing = previewVersionId === v.version_id
                || (previewVersionId === null && isCurrent);
              return (
                <VersionRow
                  key={v.version_id}
                  version={v}
                  index={idx}
                  label={isCurrent
                    ? t('Current version')
                    : t('Version {{n}}', { n: versions.length - idx })}
                  isCurrent={isCurrent}
                  selected={isPreviewing}
                  restoring={restoringId === v.version_id}
                  restoreDisabled={!!restoringId}
                  onSelect={() => selectVersion(isCurrent ? null : v.version_id)}
                  onRestore={() => restore(v.version_id)}
                  onCompare={() => setCompareId(v.version_id)}
                />
              );
            })}
          </ol>
        )}
      </div>

      {compareId && kind && uuid && currentVersionId && (
        <VersionCompareOverlay
          kind={kind}
          uuid={uuid}
          oldVersionId={compareId}
          currentVersionId={currentVersionId}
          oldLabel={compareLabel}
          restoring={restoringId === compareId}
          onRestore={() => { restore(compareId); setCompareId(null); }}
          onClose={() => setCompareId(null)}
        />
      )}
    </PaneBody>
  );
};

export default DraftingVersionsPane;
